import { FormEvent, useState } from "react";
import "./DemoRequestForm.css";

const PERSONAL_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.co.uk",
  "ymail.com",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
  "gmx.com",
  "gmx.net",
  "mail.com",
  "yandex.com",
  "yandex.ru",
  "zoho.com",
  "fastmail.com",
  "hey.com",
  "tuta.com",
  "tutanota.com",
]);

const isBusinessEmail = (email: string) => {
  const normalizedEmail = email.trim().toLowerCase();
  const match = normalizedEmail.match(/^[^\s@]+@([^\s@]+\.[^\s@]+)$/);
  return Boolean(match && !PERSONAL_EMAIL_DOMAINS.has(match[1]));
};

type DemoRequestFormProps = {
  source: "hero" | "register-interest";
  buttonText?: string;
  variant?: "hero" | "card";
};

export default function DemoRequestForm({
  source,
  buttonText = "Request free demo",
  variant = "hero",
}: DemoRequestFormProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage("");

    if (!isBusinessEmail(email)) {
      setStatus("error");
      setMessage("Please use your company email rather than a personal mailbox.");
      return;
    }

    setStatus("submitting");

    try {
      const response = await fetch("/api/demo-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          source,
          pageUrl: window.location.href,
          website: "",
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "We couldn't send your request. Please try again.");
      }

      setStatus("success");
      setMessage("Request received. We'll contact you at your company email.");
      setEmail("");
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "We couldn't send your request. Please try again.");
    }
  };

  return (
    <div className={`demo-request demo-request-${variant}`}>
      <form className="demo-request-form" onSubmit={handleSubmit} noValidate>
        <label htmlFor={`demo-email-${source}`} className="sr-only">
          Company email for your custom demo
        </label>
        <input
          id={`demo-email-${source}`}
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (status !== "idle") {
              setStatus("idle");
              setMessage("");
            }
          }}
          placeholder="Enter company email"
          aria-describedby={`demo-status-${source}`}
          aria-invalid={status === "error"}
        />
        <button type="submit" className="hero-btn hero-btn-primary" disabled={status === "submitting"}>
          {status === "submitting" ? "Sending…" : buttonText}
        </button>
      </form>
      <p
        id={`demo-status-${source}`}
        className={`demo-request-status ${status === "error" ? "is-error" : ""}`}
        role={status === "error" ? "alert" : "status"}
        aria-live="polite"
      >
        {message}
      </p>
    </div>
  );
}
